/*!
 * Copyright © 2026 Nail Alliance - North America, Inc.
 * Authors: See snippets/copyright-notices.liquid in the theme.
 * No other use is authorized without a written contract.
 */

(() => {
  const rootPath = () => window.Shopify?.routes?.root || '/';
  let cartSnapshot = null;
  let cartRequest = null;

  const getCart = () => {
    if (cartSnapshot) return Promise.resolve(cartSnapshot);
    if (!cartRequest) {
      cartRequest = fetch(`${rootPath()}cart.js`, { headers: { Accept: 'application/json' } })
        .then((response) => {
          if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
          return response.json();
        })
        .then((cart) => {
          cartSnapshot = cart;
          return cart;
        })
        .catch(() => null)
        .finally(() => {
          cartRequest = null;
        });
    }
    return cartRequest;
  };

  class NailArtsSlider extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.viewport = this.querySelector('[data-slider-viewport]');
      this.previous = this.querySelector('[data-slider-previous]');
      this.next = this.querySelector('[data-slider-next]');
      if (!this.viewport) return;

      this.previous?.addEventListener('click', () => this.move(-1), { signal });
      this.next?.addEventListener('click', () => this.move(1), { signal });
      this.viewport.addEventListener('scroll', () => this.requestUpdate(), { passive: true, signal });
      this.resizeObserver = new ResizeObserver(() => this.updateControls());
      this.resizeObserver.observe(this.viewport);
      this.updateControls();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      window.cancelAnimationFrame(this.updateFrame);
    }

    requestUpdate() {
      if (this.updateFrame) return;
      this.updateFrame = window.requestAnimationFrame(() => {
        this.updateFrame = null;
        this.updateControls();
      });
    }

    move(direction) {
      const item = this.querySelector('[data-slider-item]');
      const gap = Number.parseFloat(getComputedStyle(this.viewport).columnGap) || 20;
      const distance = (item?.getBoundingClientRect().width || this.viewport.clientWidth * 0.8) + gap;
      this.viewport.scrollBy({ left: distance * direction, behavior: 'smooth' });
    }

    updateControls() {
      const maximum = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const overflowing = maximum > 2;
      if (this.previous) {
        this.previous.hidden = !overflowing;
        this.previous.disabled = this.viewport.scrollLeft <= 2;
      }
      if (this.next) {
        this.next.hidden = !overflowing;
        this.next.disabled = this.viewport.scrollLeft >= maximum - 2;
      }
    }
  }

  class NailArtsVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.video.play().catch(() => {});
          else this.video.pause();
        });
      }, { rootMargin: '160px 0px', threshold: 0.2 });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.video?.pause();
    }
  }

  class NailArtsQuickAdd extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.form = this.querySelector('[data-nail-arts-quick-add-form]');
      this.button = this.querySelector('button[type="submit"]');
      this.variantId = Number(this.form?.querySelector('[name="id"]')?.value || 0);
      this.form?.addEventListener('submit', (event) => this.add(event), { signal: this.controller.signal });
      document.addEventListener('theme:cart-updated', (event) => this.syncCart(event.detail?.cart), { signal: this.controller.signal });
      getCart().then((cart) => this.syncCart(cart));
    }

    disconnectedCallback() {
      this.controller?.abort();
      window.clearTimeout(this.successTimer);
    }

    syncCart(cart) {
      if (!cart || !this.button || !this.variantId) return;
      cartSnapshot = cart;
      const isInCart = cart.items?.some((item) => {
        const itemVariantId = Number(item.variant_id || item.id);
        return itemVariantId === this.variantId && Number(item.quantity) > 0;
      });
      this.button.classList.toggle('is-in-cart', Boolean(isInCart));
      this.button.setAttribute('aria-pressed', String(Boolean(isInCart)));
    }

    async add(event) {
      event.preventDefault();
      if (!this.form || !this.button || this.loading) return;
      this.loading = true;
      this.button.disabled = true;
      this.button.classList.add('is-loading');
      this.button.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(`${rootPath()}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(this.form)
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.description || 'This product could not be added.');

        const cartResponse = await fetch(`${rootPath()}cart.js`, { headers: { Accept: 'application/json' } });
        const cart = cartResponse.ok ? await cartResponse.json() : null;
        if (cart) cartSnapshot = cart;
        document.dispatchEvent(new CustomEvent('theme:cart-updated', { detail: { cart } }));
        const productTitle = this.dataset.productTitle || result.product_title || result.title || '';
        const productImage = this.dataset.productImage || result.featured_image?.url || result.image || '';
        document.dispatchEvent(new CustomEvent('theme:toast', {
          detail: {
            message: productTitle ? '' : 'Added to your shopping bag.',
            type: 'success',
            label: this.dataset.productAddedLabel || 'Product added',
            product: productTitle ? { title: productTitle, image: productImage, alt: productTitle } : null
          }
        }));
        this.button.classList.remove('is-loading');
        this.button.classList.add('is-success');
        // Campaign CTAs use text, while shared product cards use a plus icon.
        // Missing icon markup must not turn a successful add into an error toast.
        const icon = this.button.querySelector('[aria-hidden="true"]');
        if (icon) icon.textContent = '✓';
        window.clearTimeout(this.successTimer);
        this.successTimer = window.setTimeout(() => {
          this.button?.classList.remove('is-success');
          if (icon) icon.textContent = '+';
        }, 900);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'This product could not be added.';
        document.dispatchEvent(new CustomEvent('theme:toast', { detail: { message, type: 'error' } }));
      } finally {
        this.loading = false;
        this.button.disabled = false;
        this.button.classList.remove('is-loading');
        this.button.removeAttribute('aria-busy');
      }
    }
  }

  if (!customElements.get('nail-arts-slider')) customElements.define('nail-arts-slider', NailArtsSlider);
  if (!customElements.get('nail-arts-video')) customElements.define('nail-arts-video', NailArtsVideo);
  if (!customElements.get('nail-arts-quick-add')) customElements.define('nail-arts-quick-add', NailArtsQuickAdd);
})();
